<template>
  <div>
    <div class="page-header">
      <button class="btn btn-secondary back-button" @click="goBack">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <div class="title-section">
        <h4 class="table-page-header invoice-title">
          {{ clientName }}, {{ authNumber }}, {{ monthDisplay }}
        </h4>
      </div>
    </div>

    <div v-if="loading" class="invoice-content">
      <div class="text-center">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p>Loading invoice details...</p>
      </div>
    </div>

    <div v-else class="invoice-content">
      <!-- Invoice Summary -->
      <div class="invoice-summary">
        <h5>Invoice Summary</h5>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">Total Shifts</div>
            <div class="summary-value">{{ shifts.length }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Hours</div>
            <div class="summary-value">{{ totalHours }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Period</div>
            <div class="summary-value">{{ dateRange }}</div>
          </div>
        </div>
      </div>

      <!-- QuickBooks Authentication Warning -->
      <div v-if="showQbAuthWarning" class="qb-warning-section">
        <div class="qb-warning-header">
          <div class="qb-warning-icon">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <div class="qb-warning-content">
            <h5>QuickBooks Not Authenticated</h5>
            <p class="qb-warning-text">
              QuickBooks is not connected. Invoice letter sequencing will default to 'A' and may not be accurate.
              Please connect to QuickBooks in Settings to ensure proper invoice numbering.
            </p>
          </div>
        </div>
      </div>

      <!-- QuickBooks Status Section -->
      <div v-if="qbInvoiceInfo" class="qb-status-section">
        <div class="qb-status-header">
          <div class="qb-logo-badge">
            <i class="fa fa-check-circle"></i>
          </div>
          <div class="qb-status-content">
            <h5>QuickBooks Invoice</h5>
            <p class="qb-status-subtitle">This invoice has been sent to QuickBooks</p>
          </div>
        </div>
        <div class="qb-info-grid">
          <div class="qb-info-item">
            <div class="qb-info-label">Invoice Number</div>
            <div class="qb-info-value">{{ qbInvoiceInfo.invoiceNumber || 'N/A' }}</div>
          </div>
          <div class="qb-info-item">
            <div class="qb-info-label">QuickBooks ID</div>
            <div class="qb-info-value">{{ qbInvoiceInfo.invoiceId }}</div>
          </div>
          <div class="qb-info-item">
            <div class="qb-info-label">Amount</div>
            <div class="qb-info-value">${{ parseFloat(qbInvoiceInfo.amount || 0).toFixed(2) }}</div>
          </div>
          <div class="qb-info-item">
            <div class="qb-info-label">Sent Date</div>
            <div class="qb-info-value">{{ formatQbDate(qbInvoiceInfo.sentAt) }}</div>
          </div>
        </div>
        <div v-if="qbInvoiceInfo.attachments && qbInvoiceInfo.attachments.length > 0" class="qb-attachments-info">
          <div class="qb-attachments-label">Attachments Status:</div>
          <div class="qb-attachment-badges">
            <span
              v-for="(attachment, index) in qbInvoiceInfo.attachments"
              :key="index"
              :class="['qb-attachment-badge', attachment.success ? 'success' : 'failed']"
            >
              <i :class="['fa', attachment.success ? 'fa-check' : 'fa-times']"></i>
              {{ attachment.type }}
            </span>
          </div>
        </div>
      </div>

      <!-- Office Send Status Section -->
      <div v-if="officeSentInfo" class="office-status-section">
        <div class="office-status-header">
          <div class="office-logo-badge">
            <i class="fa fa-check-circle"></i>
          </div>
          <div class="office-status-content">
            <h5>Sent to Office</h5>
            <p class="office-status-subtitle">Documents have been emailed to the office</p>
          </div>
        </div>
        <div class="office-info-grid">
          <div class="office-info-item">
            <div class="office-info-label">Email</div>
            <div class="office-info-value">{{ officeSentInfo.email }}</div>
          </div>
          <div class="office-info-item">
            <div class="office-info-label">Sent Date</div>
            <div class="office-info-value">{{ formatQbDate(officeSentInfo.sentAt) }}</div>
          </div>
        </div>
      </div>

      <!-- Attachments Section -->
      <div class="attachments-section">
        <div class="section-header-with-action">
          <h5>Attachments</h5>
          <div class="quickbooks-buttons-container">
            <!-- "Already sent to QB" checkbox for JD codes not yet tracked in QB -->
            <label
              v-if="isJobDevelopmentServiceCode && !hasAnyPriorQbInvoice && !qbInvoiceInfo && !isQuickBooksDisabled"
              class="already-sent-checkbox"
            >
              <input type="checkbox" v-model="markedAlreadySentToQb" />
              Already sent to QuickBooks
            </label>

            <!-- Disabled QB Button (when QB_ENVIRONMENT=none) -->
            <button
              v-if="isQuickBooksDisabled"
              class="btn btn-secondary quickbooks-button"
              disabled
              title="QuickBooks integration is disabled in the development environment"
            >
              <i class="fa fa-ban"></i>
              QuickBooks Disabled
            </button>

            <!-- Send to Office Button (subsequent JD months) -->
            <button
              v-else-if="isSubsequentJdMonth"
              class="btn btn-primary quickbooks-button office-button"
              :class="{ 'office-button-sent': officeSentInfo }"
              @click="sendToOffice"
              :disabled="sendingToOffice || officeSentInfo"
            >
              <i v-if="!sendingToOffice && !officeSentInfo" class="fa fa-envelope"></i>
              <i v-else-if="!sendingToOffice && officeSentInfo" class="fa fa-check-circle"></i>
              <i v-else class="fa fa-spinner fa-spin"></i>
              {{ sendingToOffice ? 'Sending...' : (officeSentInfo ? 'Sent to Office' : 'Send to Office') }}
            </button>

            <!-- Production QB Button (only in production environment) -->
            <button
              v-else-if="showProductionButton"
              class="btn btn-primary quickbooks-button"
              :class="{ 'quickbooks-button-sent': qbInvoiceInfo }"
              @click="sendToQuickBooks('production')"
              :disabled="sendingToQuickBooks || qbInvoiceInfo"
            >
              <i v-if="!sendingToQuickBooks && !qbInvoiceInfo" class="fa fa-paper-plane"></i>
              <i v-else-if="!sendingToQuickBooks && qbInvoiceInfo" class="fa fa-check-circle"></i>
              <i v-else class="fa fa-spinner fa-spin"></i>
              {{ sendingToQuickBooks ? 'Sending...' : (qbInvoiceInfo ? 'Sent to QuickBooks' : 'Send to QuickBooks') }}
            </button>

            <!-- Sandbox QB Button (only in test/sandbox environment) -->
            <button
              v-else-if="showSandboxButton"
              class="btn btn-secondary quickbooks-button sandbox-button"
              :class="{ 'quickbooks-button-sent': qbInvoiceInfo }"
              @click="sendToQuickBooks('sandbox')"
              :disabled="sendingToSandbox || qbInvoiceInfo"
            >
              <i v-if="!sendingToSandbox && !qbInvoiceInfo" class="fa fa-flask"></i>
              <i v-else-if="!sendingToSandbox && qbInvoiceInfo" class="fa fa-check-circle"></i>
              <i v-else class="fa fa-spinner fa-spin"></i>
              {{ sendingToSandbox ? 'Sending...' : (qbInvoiceInfo ? 'Sent to Sandbox' : 'Send to Sandbox QB') }}
            </button>
          </div>
        </div>
        <div class="attachments-grid">
          <!-- Original Authorization -->
          <div class="attachment-item">
            <div class="attachment-icon">
              <i class="fa fa-file-pdf"></i>
            </div>
            <div class="attachment-info">
              <div class="attachment-name">Original Authorization</div>
              <div class="attachment-description">View the uploaded authorization document</div>
            </div>
            <button
              v-if="authorization?.pdf_filename"
              class="btn btn-sm btn-primary attachment-action"
              @click="viewAuthorizationPdf"
            >
              <i class="fa fa-eye"></i> View
            </button>
            <span v-else class="attachment-unavailable">Not Available</span>
          </div>

          <!-- Generated Form (only show if service code supports forms) -->
          <div v-if="expectedFormType" class="attachment-item">
            <div class="attachment-icon">
              <i class="fa fa-file-pdf"></i>
            </div>
            <div class="attachment-info">
              <div class="attachment-name">{{ formDisplayName }}</div>
              <div class="attachment-description">View, download, or upload an updated version</div>
            </div>
            <div v-if="generatedForm" class="attachment-actions">
              <button
                class="btn btn-sm btn-primary attachment-action"
                @click="viewGeneratedForm"
              >
                <i class="fa fa-eye"></i> View
              </button>
              <button
                class="btn btn-sm btn-secondary attachment-action"
                @click="downloadGeneratedForm"
              >
                <i class="fa fa-download"></i> Download
              </button>
              <button
                class="btn btn-sm btn-success attachment-action"
                @click="triggerUpload"
              >
                <i class="fa fa-upload"></i> Replace
              </button>
              <button
                class="btn btn-sm btn-warning attachment-action"
                @click="regenerateForm"
                :disabled="regeneratingForm"
                title="Regenerate this form"
              >
                <i v-if="!regeneratingForm" class="fa fa-sync"></i>
                <i v-else class="fa fa-spinner fa-spin"></i>
                {{ regeneratingForm ? 'Regenerating...' : 'Regenerate' }}
              </button>
              <input
                ref="fileInput"
                type="file"
                accept="application/pdf"
                style="display: none"
                @change="handleFileUpload"
              />
            </div>
            <span v-else class="attachment-unavailable">Not Yet Generated</span>
          </div>

          <!-- Invoice Document (hidden for Job Development service codes) -->
          <div v-if="!isJobDevelopmentServiceCode" class="attachment-item">
            <div class="attachment-icon">
              <i class="fa fa-file-invoice-dollar"></i>
            </div>
            <div class="attachment-info">
              <div class="attachment-name">Invoice</div>
              <div class="attachment-description">View, download, or upload an updated version</div>
            </div>
            <div v-if="invoiceDocument" class="attachment-actions">
              <button
                class="btn btn-sm btn-primary attachment-action"
                @click="viewInvoice"
              >
                <i class="fa fa-eye"></i> View
              </button>
              <button
                class="btn btn-sm btn-secondary attachment-action"
                @click="downloadInvoice"
              >
                <i class="fa fa-download"></i> Download
              </button>
              <button
                class="btn btn-sm btn-success attachment-action"
                @click="triggerInvoiceUpload"
              >
                <i class="fa fa-upload"></i> Replace
              </button>
              <button
                v-if="!invoiceCreationDisabled"
                class="btn btn-sm btn-warning attachment-action"
                @click="regenerateInvoice"
                :disabled="regeneratingInvoice"
                title="Regenerate this invoice"
              >
                <i v-if="!regeneratingInvoice" class="fa fa-sync"></i>
                <i v-else class="fa fa-spinner fa-spin"></i>
                {{ regeneratingInvoice ? 'Regenerating...' : 'Regenerate' }}
              </button>
              <input
                ref="invoiceFileInput"
                type="file"
                accept="application/pdf"
                style="display: none"
                @change="handleInvoiceUpload"
              />
            </div>
            <span v-else-if="invoiceSkippedDueToPriorInvoice" class="attachment-not-needed">
              Not needed - invoices are not required for Job Development service codes
            </span>
            <span v-else-if="invoiceCreationDisabled" class="attachment-not-needed">
              Not needed - invoice creation is disabled for this service code
            </span>
            <span v-else class="attachment-unavailable">Not Yet Generated</span>
          </div>

          <!-- Additional Attachments -->
          <div v-for="attachment in additionalAttachments" :key="attachment.id" class="attachment-item">
            <div class="attachment-icon">
              <i class="fa fa-paperclip"></i>
            </div>
            <div class="attachment-info">
              <div class="attachment-name">{{ getAttachmentDisplayName(attachment.link) }}</div>
              <div class="attachment-description">Additional attachment</div>
            </div>
            <div class="attachment-actions">
              <button
                class="btn btn-sm btn-primary attachment-action"
                @click="viewAdditionalAttachment(attachment)"
              >
                <i class="fa fa-eye"></i> View
              </button>
              <button
                class="btn btn-sm btn-danger attachment-action"
                @click="deleteAdditionalAttachment(attachment)"
              >
                <i class="fa fa-trash"></i> Delete
              </button>
            </div>
          </div>

          <!-- Upload Additional Attachment -->
          <div class="attachment-item upload-new">
            <div class="attachment-icon">
              <i class="fa fa-plus-circle"></i>
            </div>
            <div class="attachment-info">
              <div class="attachment-name">Add Additional Attachment</div>
              <div class="attachment-description">Upload additional documents for this invoice</div>
            </div>
            <div class="attachment-actions">
              <button
                class="btn btn-sm btn-success attachment-action"
                @click="triggerAdditionalAttachmentUpload"
                :disabled="uploadingAdditionalAttachment"
              >
                <i v-if="!uploadingAdditionalAttachment" class="fa fa-upload"></i>
                <i v-else class="fa fa-spinner fa-spin"></i>
                {{ uploadingAdditionalAttachment ? 'Uploading...' : 'Upload' }}
              </button>
              <input
                ref="additionalAttachmentInput"
                type="file"
                accept="application/pdf"
                style="display: none"
                @change="handleAdditionalAttachmentUpload"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Shifts List -->
      <div class="shifts-section">
        <h5>Shifts</h5>

        <div v-if="shifts.length === 0" class="no-data">
          <p>No shifts found for this invoice period</p>
        </div>

        <div v-else class="shifts-table-container">
          <table class="shifts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Caregiver</th>
                <th>Service Code</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Hours</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="shift in shifts" :key="shift.id" class="clickable-row" @click="viewShiftDetails(shift)">
                <td>{{ formatShiftDate(shift.scheduled_start) }}</td>
                <td>{{ formatDayOfWeek(shift.scheduled_start) }}</td>
                <td>{{ formatCaregivers(shift.caregivers) }}</td>
                <td>
                  <div class="service-codes">
                    <span
                      v-for="sc in shift.service_codes"
                      :key="sc.id"
                      class="service-code-badge"
                    >
                      {{ sc.code }}
                    </span>
                  </div>
                </td>
                <td>{{ formatTime(shift.clock_in) }}</td>
                <td>{{ formatTime(shift.clock_out) }}</td>
                <td class="hours-cell">
                  {{ shift.hours_applied_to_auth || shift.billing_hours || '0.00' }}
                </td>
                <td>
                  <button
                    v-if="shift.shift_notes"
                    class="btn btn-sm btn-link notes-button"
                    @click="showNotes(shift)"
                  >
                    <i class="fa fa-file-alt"></i>
                  </button>
                  <span v-else class="no-notes">-</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="6" class="total-label">Total Hours:</td>
                <td class="total-value">{{ totalHours }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Notes Modal -->
    <div v-if="notesModal.show" class="modal-overlay" @click="closeNotesModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>Shift Notes</h5>
          <button class="close-button" @click="closeNotesModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="shift-info">
            <p><strong>Date:</strong> {{ formatShiftDate(notesModal.shift?.scheduled_start) }}</p>
            <p><strong>Caregiver:</strong> {{ formatCaregivers(notesModal.shift?.caregivers) }}</p>
          </div>
          <div class="notes-content">
            {{ notesModal.shift?.shift_notes }}
          </div>
        </div>
      </div>
    </div>

    <!-- Shift Action Modal -->
    <ShiftActionModal
      :visible.sync="showShiftActionModal"
      :shift="selectedShiftForActions"
      @undo-approval="handleUndoApproval"
      @edit-shift-notes="handleEditShiftNotes"
    />

    <EditShiftNotesModal
      :visible.sync="showEditShiftNotesModal"
      :shift="shiftToEditNotes"
      @updated="loadInvoiceShifts"
    />

    <!-- PDF Viewer Modal -->
    <AIVDialog
      v-model="$modal.state.view_invoice_pdf"
      :title="selectedPdfName"
      width="90%"
      :no-body-padding="true"
      :no-body-overflow="true"
    >
      <div class="pdf-viewer-container">
        <div v-if="loadingPdf" class="text-center">
          <i class="fa fa-spinner fa-spin"></i> Loading PDF...
        </div>
        <iframe
          v-else-if="pdfUrl"
          :src="pdfUrl"
          class="pdf-viewer-frame"
          type="application/pdf"
        />
        <div v-else class="no-pdf">
          <p>Unable to load PDF.</p>
        </div>
      </div>
    </AIVDialog>
  </div>
</template>

<script>
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { authorizationsService } from "@/serviceHandlers/authorizationsService";
import { documentsService } from "@/serviceHandlers/documentsService";
import { quickbooksService } from "@/serviceHandlers/quickbooksService";
import { officesService } from "@/serviceHandlers/officesService";
import { getFormKeyForType } from "@/constants/billingForms";
import ShiftActionModal from "@/components/ShiftActionModal.vue";
import EditShiftNotesModal from "@/components/EditShiftNotesModal.vue";

export default {
  name: "InvoiceDetails",
  components: { ShiftActionModal, EditShiftNotesModal },
  data() {
    return {
      authorizationId: null,
      authNumber: "",
      clientName: "",
      monthDisplay: "",
      year: null,
      month: null,
      loading: false,
      shifts: [],
      authorization: null,
      generatedDocuments: [],
      notesModal: {
        show: false,
        shift: null,
      },
      pdfUrl: null,
      loadingPdf: false,
      selectedPdfName: "",
      sendingToQuickBooks: false,
      sendingToSandbox: false,
      pendingQuickBooksInvoice: false,
      pendingConnectionType: null,
      qbInvoiceInfo: null,
      loadingQbInfo: false,
      uploadingAdditionalAttachment: false,
      qbStatus: null, // {production: {...}, sandbox: {...}, isTestEnvironment: boolean}
      regeneratingForm: false,
      regeneratingInvoice: false,
      priorInvoiceExists: false,
      showShiftActionModal: false,
      selectedShiftForActions: null,
      showEditShiftNotesModal: false,
      shiftToEditNotes: null,
      hasAnyPriorQbInvoice: false,
      officeSentInfo: null,
      sendingToOffice: false,
      markedAlreadySentToQb: false,
    };
  },
  computed: {
    totalHours() {
      const total = this.shifts.reduce((sum, shift) => {
        return sum + parseFloat(shift.hours_applied_to_auth || shift.billing_hours || 0);
      }, 0);
      return total.toFixed(2);
    },
    dateRange() {
      if (this.shifts.length === 0) return 'N/A';

      const dates = this.shifts
        .map(s => new Date(s.scheduled_start))
        .sort((a, b) => a - b);

      const firstDate = dates[0];
      const lastDate = dates[dates.length - 1];

      const formatDate = (date) => date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      return `${formatDate(firstDate)} - ${formatDate(lastDate)}`;
    },
    // Determine which USOR form type to show based on the service code's Type
    // (resolved via the billing form registry).
    expectedFormType() {
      return getFormKeyForType(this.authorization?.service_code?.type);
    },
    // Get the generated document for this form type if it exists
    generatedForm() {
      if (!this.expectedFormType) return null;
      return this.generatedDocuments.find(doc => doc.type === this.expectedFormType);
    },
    // Get form display name
    formDisplayName() {
      if (!this.expectedFormType) return null;

      const names = {
        'USOR95': 'USOR 95 - Job Coaching Tracker',
        'USOR96': 'USOR 96 - Job Development',
        'USOR148': 'USOR 148 - Life Skills / Service Brokering / Supported Education'
      };

      return names[this.expectedFormType];
    },
    // Get the invoice document if it exists
    invoiceDocument() {
      return this.generatedDocuments.find(doc => doc.type === 'Invoice');
    },
    // Check if this is a Job Development (USOR96) service code, which never
    // requires an invoice (one invoice per auth handled separately).
    isJobDevelopmentServiceCode() {
      return getFormKeyForType(this.authorization?.service_code?.type) === 'USOR96';
    },
    // True when the service code is configured to not create invoices
    // (Settings → Service Codes → "Create Invoice" unchecked).
    invoiceCreationDisabled() {
      return this.authorization?.service_code?.create_invoice === false;
    },
    // Check if invoice is not needed because this is a Job Development service code
    // Job Development service codes (CE, SE, SJBT, TWE) never require invoices
    invoiceSkippedDueToPriorInvoice() {
      return this.isJobDevelopmentServiceCode && !this.invoiceDocument;
    },
    // Get additional attachments
    additionalAttachments() {
      return this.generatedDocuments.filter(doc => doc.type === 'AdditionalAttachment');
    },
    // Check if this is a subsequent JD month (should send to office instead of QB)
    // Only show "Send to Office" if the invoice hasn't already been sent to QB (including sandbox)
    isSubsequentJdMonth() {
      return this.isJobDevelopmentServiceCode && (this.hasAnyPriorQbInvoice || this.markedAlreadySentToQb) && !this.qbInvoiceInfo;
    },
    // Check if QuickBooks is disabled (QB_ENVIRONMENT=none)
    isQuickBooksDisabled() {
      return this.qbStatus && this.qbStatus.isDisabled;
    },
    // Check if we're in test/sandbox environment and should show sandbox button
    showSandboxButton() {
      // Don't show if disabled or if we don't have status
      if (!this.qbStatus || this.qbStatus.isDisabled) return false;
      // Show in test or sandbox environment (including when invoice already sent, to show "Sent to Sandbox")
      return this.qbStatus.isTestEnvironment || this.qbStatus.currentEnvironment === 'sandbox';
    },
    // Check if we should show production button
    showProductionButton() {
      // If explicitly disabled, don't show
      if (this.qbStatus && this.qbStatus.isDisabled) return false;

      // Show production button if:
      // 1. No status loaded yet (fallback for safety) OR
      // 2. In production environment
      return !this.qbStatus || this.qbStatus.currentEnvironment === 'production';
    },
    // Check if production connection is authenticated
    isProductionAuthenticated() {
      return this.qbStatus && this.qbStatus.production && this.qbStatus.production.authenticated;
    },
    // Check if sandbox connection is authenticated
    isSandboxAuthenticated() {
      return this.qbStatus && this.qbStatus.sandbox && this.qbStatus.sandbox.authenticated;
    },
    // Show QB authentication warning
    showQbAuthWarning() {
      // Don't show if QB is disabled or if status hasn't loaded
      if (!this.qbStatus || this.qbStatus.isDisabled) return false;

      // In production environment, check production auth
      if (this.qbStatus.currentEnvironment === 'production') {
        return !this.isProductionAuthenticated;
      }

      // In sandbox/test environment, check sandbox auth
      if (this.qbStatus.currentEnvironment === 'sandbox' || this.qbStatus.isTestEnvironment) {
        return !this.isSandboxAuthenticated;
      }

      return false;
    }
  },
  async mounted() {
    this.authorizationId = this.$route.params.authorizationId;
    this.year = parseInt(this.$route.params.year);
    this.month = parseInt(this.$route.params.month);
    this.authNumber = this.$route.query.authNumber || "Unknown";
    this.clientName = this.$route.query.clientName || "Unknown";
    this.monthDisplay = this.$route.query.monthDisplay || "Unknown";

    // Listen for QuickBooks OAuth messages from popup
    window.addEventListener('message', this.handleQuickBooksMessage);

    await Promise.all([
      this.loadInvoiceShifts(),
      this.loadAuthorization(),
      this.loadGeneratedDocuments(),
      this.loadQuickBooksInvoiceInfo(),
      this.loadQuickBooksStatus()
    ]);
  },
  beforeDestroy() {
    // Clean up PDF URLs
    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
    }
    if (this.pdfEditorUrl) {
      URL.revokeObjectURL(this.pdfEditorUrl);
    }

    // Remove QuickBooks message listener
    window.removeEventListener('message', this.handleQuickBooksMessage);
  },
  methods: {
    async loadInvoiceShifts() {
      this.loading = true;
      try {
        const response = await shiftsService.getApprovedShiftsByAuthorization(
          this.authorizationId
        );

        if (response && response.shifts) {
          // Filter shifts for this specific month
          this.shifts = response.shifts
            .filter(shift => {
              if (!shift.scheduled_start) return false;
              const date = new Date(shift.scheduled_start);
              return date.getFullYear() === this.year &&
                     date.getMonth() + 1 === this.month;
            })
            .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
        }
      } catch (error) {
        this.$notifyError("Error loading invoice details. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    async loadAuthorization() {
      try {
        const response = await authorizationsService.getById(this.authorizationId);
        if (response) {
          this.authorization = response;
        }
      } catch (error) {
        // Error loading authorization
      }
    },

    async loadGeneratedDocuments() {
      try {
        const response = await documentsService.getByAuthAndMonth(
          this.authorizationId,
          this.month,
          this.year
        );
        if (response) {
          if (response.documents) {
            this.generatedDocuments = response.documents;
          }
          // Track if a prior invoice exists for this authorization (from a different month)
          this.priorInvoiceExists = response.priorInvoiceExists || false;
        }
      } catch (error) {
        // Error loading generated documents
      }
    },

    async loadQuickBooksInvoiceInfo() {
      this.loadingQbInfo = true;
      try {
        const response = await quickbooksService.getInvoiceInfo(
          this.authorizationId,
          this.month,
          this.year
        );
        if (response) {
          if (response.sent) {
            this.qbInvoiceInfo = response;
          }
          this.hasAnyPriorQbInvoice = response.hasAnyPriorQbInvoice || false;
          this.officeSentInfo = response.officeSentInfo || null;
        }
      } catch (error) {
        // Error loading QuickBooks invoice info
      } finally {
        this.loadingQbInfo = false;
      }
    },

    async loadQuickBooksStatus() {
      try {
        const response = await quickbooksService.getStatus();
        if (response) {
          this.qbStatus = response;
        }
      } catch (error) {
        // Error loading QuickBooks status
        this.$notifyError('Failed to check QuickBooks authentication status.');
      }
    },

    async viewAuthorizationPdf() {
      if (!this.authorization?.pdf_filename) {
        this.$notifyError("Original authorization PDF not available.");
        return;
      }

      await this.viewPdf(
        this.authorization.pdf_filename,
        'secure',
        'Original Authorization'
      );
    },

    async viewGeneratedForm() {
      if (!this.generatedForm) {
        this.$notifyError("Form has not been generated yet.");
        return;
      }

      // Extract just the filename from the link
      const filename = this.generatedForm.link.split('/').pop();
      await this.viewPdf(
        filename,
        'client-document',
        this.formDisplayName
      );
    },

    async viewInvoice() {
      if (!this.invoiceDocument) {
        this.$notifyError("Invoice has not been generated yet.");
        return;
      }

      // Extract just the filename from the link
      const filename = this.invoiceDocument.link.split('/').pop();
      await this.viewPdf(
        filename,
        'client-document',
        'Invoice'
      );
    },

    async downloadInvoice() {
      if (!this.invoiceDocument) {
        this.$notifyError("Invoice has not been generated yet.");
        return;
      }

      try {
        // Extract just the filename from the link
        const filename = this.invoiceDocument.link.split('/').pop();

        // Use the authenticated API endpoint
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;

        // Get JWT token from localStorage
        const token = localStorage.getItem('jwtToken');

        // Fetch the PDF with authentication
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
        }

        // Convert to blob and trigger download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$notifyError('Error downloading invoice: ' + error.message);
      }
    },

    async downloadGeneratedForm() {
      if (!this.generatedForm) {
        this.$notifyError("Form has not been generated yet.");
        return;
      }

      try {
        // Extract just the filename from the link
        const filename = this.generatedForm.link.split('/').pop();

        // Use the authenticated API endpoint
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;

        // Get JWT token from localStorage
        const token = localStorage.getItem('jwtToken');

        // Fetch the PDF with authentication
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
        }

        // Convert to blob and trigger download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$notifyError('Error downloading PDF: ' + error.message);
      }
    },

    triggerUpload() {
      // Trigger the hidden file input
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      // Validate file type
      if (file.type !== 'application/pdf') {
        this.$notifyError('Please select a PDF file.');
        event.target.value = ''; // Reset file input
        return;
      }

      try {
        // Extract the filename from the generated form
        const filename = this.generatedForm.link.split('/').pop();

        // Use the authenticated API endpoint
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;

        // Get JWT token from localStorage
        const token = localStorage.getItem('jwtToken');

        // Create FormData to upload the file
        const formData = new FormData();
        formData.append('pdf', file, filename);

        // Upload the PDF with authentication
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Authorization': token
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to upload PDF');
        }

        this.$notifySuccess('PDF uploaded successfully!');

        // Reset file input
        event.target.value = '';
      } catch (error) {
        this.$notifyError('Error uploading PDF: ' + error.message);
        event.target.value = ''; // Reset file input
      }
    },

    triggerInvoiceUpload() {
      // Trigger the hidden invoice file input
      this.$refs.invoiceFileInput.click();
    },

    async handleInvoiceUpload(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      // Validate file type
      if (file.type !== 'application/pdf') {
        this.$notifyError('Please select a PDF file.');
        event.target.value = ''; // Reset file input
        return;
      }

      try {
        // Extract the filename from the invoice document
        const filename = this.invoiceDocument.link.split('/').pop();

        // Use the authenticated API endpoint
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;

        // Get JWT token from localStorage
        const token = localStorage.getItem('jwtToken');

        // Create FormData to upload the file
        const formData = new FormData();
        formData.append('pdf', file, filename);

        // Upload the PDF with authentication
        const response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Authorization': token
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to upload invoice');
        }

        this.$notifySuccess('Invoice uploaded successfully!');

        // Reset file input
        event.target.value = '';
      } catch (error) {
        this.$notifyError('Error uploading invoice: ' + error.message);
        event.target.value = ''; // Reset file input
      }
    },

    async viewPdf(filename, endpoint, displayName) {
      try {
        // Clean up any existing PDF URL
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl);
          this.pdfUrl = null;
        }

        this.selectedPdfName = displayName;
        this.loadingPdf = true;
        this.$modal.open("view_invoice_pdf");

        // Use the authenticated API endpoint
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/pdf/${endpoint}/${encodeURIComponent(filename)}`;

        // Get JWT token from localStorage
        const token = localStorage.getItem('jwtToken');

        // Fetch the PDF with authentication
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
        }

        // Convert to blob and create object URL
        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        this.pdfUrl = URL.createObjectURL(pdfBlob);
        this.loadingPdf = false;
      } catch (error) {
        this.$notifyError('Error loading PDF: ' + error.message);
        this.$modal.close("view_invoice_pdf");
        this.loadingPdf = false;
      }
    },

    formatShiftDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    formatDayOfWeek(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    },

    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatCaregivers(caregivers) {
      if (!caregivers || caregivers.length === 0) return 'N/A';
      return caregivers.map(c => c.name).join(', ');
    },

    showNotes(shift) {
      this.notesModal = {
        show: true,
        shift: shift,
      };
    },

    closeNotesModal() {
      this.notesModal = {
        show: false,
        shift: null,
      };
    },

    formatQbDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    handleQuickBooksMessage(event) {
      // Handle messages from QuickBooks OAuth popup
      if (event.data && event.data.type) {
        if (event.data.type === 'quickbooks-auth-success') {
          const connectionType = event.data.connectionType || 'production';
          // If there's a pending invoice send, mention it in the notification
          if (this.pendingQuickBooksInvoice) {
            const qbType = connectionType === 'sandbox' ? 'QuickBooks Sandbox' : 'QuickBooks';
            this.$notifySuccess(`Successfully connected to ${qbType}! Sending invoice now...`);
            this.pendingQuickBooksInvoice = false;
            // Wait a moment for the success notification to be visible
            setTimeout(() => {
              this.sendToQuickBooks(this.pendingConnectionType || connectionType);
              this.pendingConnectionType = null;
            }, 1000);
          } else {
            this.$notifySuccess(event.data.message);
            // Reload QB status to update button states
            this.loadQuickBooksStatus();
          }
        } else if (event.data.type === 'quickbooks-auth-error') {
          this.$notifyError('QuickBooks connection failed: ' + event.data.message);
          this.pendingQuickBooksInvoice = false;
          this.pendingConnectionType = null;
        }
      }
    },

    viewShiftDetails(shift) {
      this.selectedShiftForActions = {
        ...shift,
        date: shift.scheduled_start,
        status: "approved",
        startTime: shift.scheduled_start,
        endTime: shift.scheduled_end,
        originalData: {
          id: shift.id,
          clock_in: shift.clock_in,
          clock_out: shift.clock_out,
          scheduled_start: shift.scheduled_start,
          scheduled_end: shift.scheduled_end,
          caregivers: shift.caregivers || [],
          clients: shift.clients || [],
        },
      };
      this.showShiftActionModal = true;
    },

    handleEditShiftNotes(shift) {
      // The action-modal shift keeps the full API row at the top level
      // (service_codes, payer, clients, status), which is exactly what
      // EditShiftNotesModal reads — pass it through unchanged.
      this.shiftToEditNotes = shift;
      this.showEditShiftNotesModal = true;
    },

    async handleUndoApproval(shift) {
      try {
        await this.$confirm(
          'Are you sure you want to undo this shift approval? This will restore hours back to the authorization and revert the shift to "Clocked Out" status.',
          "Confirm Undo Approval",
          {
            confirmButtonText: "Yes, Undo Approval",
            cancelButtonText: "Cancel",
            type: "warning",
          }
        );
        await shiftsService.undoShiftApproval({
          shiftId: shift.originalData.id,
        });
        this.$notifySuccess(
          "Shift approval undone successfully! Authorization hours have been restored."
        );
        this.showShiftActionModal = false;
        await this.loadInvoiceShifts();
      } catch (error) {
        if (error !== "cancel") {
          this.$notifyError(
            error.response?.data?.error || "Failed to undo shift approval"
          );
        }
      }
    },

    goBack() {
      // Check if we're in a client context
      const isClientContext = this.$route.name === "ClientInvoiceDetails";
      const routeName = isClientContext ? "ClientAuthorizationDetails" : "AuthorizationDetails";

      const params = { id: this.authorizationId };

      // Add clientId if in client context
      if (isClientContext) {
        params.clientId = this.$route.params.clientId;
      }

      this.$router.push({
        name: routeName,
        params,
        query: {
          authNumber: this.authNumber,
          clientName: this.clientName,
          // Preserve the origin so the next Back still returns there
          from: this.$route.query.from,
        },
      });
    },

    async sendToQuickBooks(connectionType = 'production') {
      try {
        // Set loading state based on connection type
        if (connectionType === 'sandbox') {
          this.sendingToSandbox = true;
        } else {
          this.sendingToQuickBooks = true;
        }

        // Try to send invoice directly to QuickBooks
        const result = await quickbooksService.sendInvoice(
          this.authorizationId,
          this.month,
          this.year,
          connectionType
        );

        if (result && result.success) {
          // Show success message with details
          const qbType = connectionType === 'sandbox' ? 'QuickBooks Sandbox' : 'QuickBooks';
          let message = `Invoice sent to ${qbType} successfully!`;

          if (result.attachments && result.attachments.length > 0) {
            const successfulAttachments = result.attachments.filter(a => a.success);
            const failedAttachments = result.attachments.filter(a => !a.success);

            if (successfulAttachments.length > 0) {
              message += ` (${successfulAttachments.length} attachment(s) uploaded)`;
            }

            if (failedAttachments.length > 0) {
              message += ` Note: ${failedAttachments.length} attachment(s) failed to upload.`;
            }
          }

          this.$notifySuccess(message);

          // Reload QB invoice info to show the updated status
          await this.loadQuickBooksInvoiceInfo();
        } else {
          throw new Error(`Failed to send invoice to ${connectionType === 'sandbox' ? 'Sandbox' : 'QuickBooks'}`);
        }
      } catch (error) {
        // Error sending to QuickBooks

        // Check if the error is due to authentication
        const isAuthError = error.response?.status === 401 ||
                           error.response?.data?.needsAuth ||
                           (error.response?.data?.message && error.response.data.message.includes('not authenticated'));

        if (isAuthError) {
          // Not authenticated - open QuickBooks connection window directly
          // Set flag to retry sending invoice after auth completes
          this.pendingQuickBooksInvoice = true;
          this.pendingConnectionType = connectionType;

          // Initiate OAuth flow immediately
          quickbooksService.initiateAuth(connectionType);
          return;
        }

        // Handle other errors
        const qbType = connectionType === 'sandbox' ? 'QuickBooks Sandbox' : 'QuickBooks';
        let errorMessage = `Failed to send invoice to ${qbType}`;

        if (error.response?.data?.message) {
          errorMessage += ': ' + error.response.data.message;
        } else if (error.message) {
          errorMessage += ': ' + error.message;
        }

        // Add details if available (like QuickBooks API error details)
        if (error.response?.data?.details) {
          const details = error.response.data.details;
          if (typeof details === 'string') {
            errorMessage += ` (${details})`;
          } else if (details.Fault?.Error?.[0]?.Message) {
            errorMessage += ` (${details.Fault.Error[0].Message})`;
          }
        }

        this.$notifyError(errorMessage);
      } finally {
        if (connectionType === 'sandbox') {
          this.sendingToSandbox = false;
        } else {
          this.sendingToQuickBooks = false;
        }
      }
    },

    async sendToOffice() {
      try {
        this.sendingToOffice = true;

        const result = await quickbooksService.sendToOffice(
          this.authorizationId,
          this.month,
          this.year
        );

        if (result && result.success) {
          this.$notifySuccess(`Documents sent to ${result.email} successfully!`);
          await this.loadQuickBooksInvoiceInfo();
        } else {
          throw new Error('Failed to send documents to office');
        }
      } catch (error) {
        let errorMessage = 'Failed to send documents to office';
        if (error.response?.data?.message) {
          errorMessage += ': ' + error.response.data.message;
        } else if (error.message) {
          errorMessage += ': ' + error.message;
        }
        this.$notifyError(errorMessage);
      } finally {
        this.sendingToOffice = false;
      }
    },

    getAttachmentDisplayName(link) {
      // Extract filename from the link and make it more readable
      const filename = link.split('/').pop();
      // Remove the Auth prefix (e.g., "AuthV1001915_2025-11_") to show just the original filename
      return filename.replace(/^Auth[^_]+_\d{4}-\d{2}_/, '');
    },

    async viewAdditionalAttachment(attachment) {
      // Extract just the filename from the link
      const filename = attachment.link.split('/').pop();
      await this.viewPdf(
        filename,
        'client-document',
        this.getAttachmentDisplayName(attachment.link)
      );
    },

    triggerAdditionalAttachmentUpload() {
      this.$refs.additionalAttachmentInput.click();
    },

    async handleAdditionalAttachmentUpload(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      // Validate file type
      if (file.type !== 'application/pdf') {
        this.$notifyError('Please select a PDF file.');
        event.target.value = '';
        return;
      }

      this.uploadingAdditionalAttachment = true;

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const apiUrl = `${apiBaseUrl}/api/documents/upload-additional-attachment`;

        const token = localStorage.getItem('jwtToken');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('authorizationId', this.authorizationId);
        formData.append('month', this.month);
        formData.append('year', this.year);

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': token
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to upload attachment');
        }

        // Reload documents to show the new attachment first
        await this.loadGeneratedDocuments();

        // Reset file input
        event.target.value = '';

        // Show success message after reload
        this.$notifySuccess('Additional attachment uploaded successfully!');
      } catch (error) {
        this.$notifyError('Error uploading attachment: ' + error.message);
        event.target.value = '';
      } finally {
        this.uploadingAdditionalAttachment = false;
      }
    },

    async deleteAdditionalAttachment(attachment) {
      this.$confirmAction(
        async () => {
          try {
            const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
            const apiUrl = `${apiBaseUrl}/api/documents/additional-attachment/${attachment.id}`;

            const token = localStorage.getItem('jwtToken');

            const response = await fetch(apiUrl, {
              method: 'DELETE',
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.message || 'Failed to delete attachment');
            }

            // Reload documents to remove the deleted attachment from the list
            await this.loadGeneratedDocuments();

            this.$notifySuccess('Additional attachment deleted successfully!');
          } catch (error) {
            this.$notifyError('Error deleting attachment: ' + error.message);
          }
        },
        'Are you sure you want to delete this additional attachment? This action cannot be undone.'
      );
    },

    async regenerateForm() {
      if (!this.expectedFormType) {
        this.$notifyError('No form type available for this authorization');
        return;
      }

      this.regeneratingForm = true;
      try {
        this.$spinner.show();
        const response = await officesService.regenerateUsorForm(
          this.authorizationId,
          this.month,
          this.year,
          this.expectedFormType
        );

        this.$notifySuccess(`Successfully regenerated ${this.expectedFormType}`);

        // Reload documents to show the new form
        await this.loadGeneratedDocuments();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        this.$notifyError(`Error regenerating form: ${errorMessage}`);
      } finally {
        this.regeneratingForm = false;
        this.$spinner.hide();
      }
    },

    async regenerateInvoice() {
      this.regeneratingInvoice = true;
      try {
        this.$spinner.show();
        const response = await officesService.regenerateInvoice(
          this.authorizationId,
          this.month,
          this.year
        );

        let successMessage = 'Successfully regenerated invoice';
        if (response.sequenceLetter) {
          successMessage += ` with sequence letter ${response.sequenceLetter}`;
        }
        this.$notifySuccess(successMessage);

        // Reload documents to show the new invoice
        await this.loadGeneratedDocuments();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        this.$notifyError(`Error regenerating invoice: ${errorMessage}`);
      } finally {
        this.regeneratingInvoice = false;
        this.$spinner.hide();
      }
    },
  },
};
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.back-button {
  font-size: 14px;
  padding: 8px 16px;
  height: 40px;
}

.back-button i {
  margin-right: 6px;
}

.title-section {
  flex: 1;
}

.invoice-title {
  margin: 0 0 0.25rem 0;
  flex: 0 0 auto;
}

.invoice-content {
  color: #fff;
}

.text-center {
  text-align: center;
  padding: 3rem 0;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

.text-center i {
  color: #66ccff;
  margin-bottom: 1rem;
}

.text-center p {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 1rem;
}

.invoice-summary {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.invoice-summary h5 {
  margin: 0 0 1.25rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
}

.qb-warning-section {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.1) 0%, rgba(246, 173, 85, 0.05) 100%);
  border: 1px solid rgba(237, 137, 54, 0.4);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.qb-warning-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.qb-warning-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(237, 137, 54, 0.2);
  border: 2px solid rgba(237, 137, 54, 0.4);
  border-radius: 8px;
  color: #ed8936;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.qb-warning-content {
  flex: 1;
}

.qb-warning-content h5 {
  margin: 0 0 0.5rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.qb-warning-text {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.5;
}

.qb-status-section {
  background: linear-gradient(135deg, rgba(44, 160, 28, 0.1) 0%, rgba(72, 187, 120, 0.05) 100%);
  border: 1px solid rgba(44, 160, 28, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.qb-status-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(44, 160, 28, 0.2);
}

.qb-logo-badge {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(44, 160, 28, 0.2);
  border: 2px solid rgba(44, 160, 28, 0.4);
  border-radius: 8px;
  color: #48bb78;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.qb-status-content {
  flex: 1;
}

.qb-status-content h5 {
  margin: 0 0 0.25rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.qb-status-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.qb-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.25rem;
}

.qb-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.qb-info-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.qb-info-value {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.qb-attachments-info {
  padding-top: 1rem;
  border-top: 1px solid rgba(44, 160, 28, 0.2);
}

.qb-attachments-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.qb-attachment-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.qb-attachment-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
}

.qb-attachment-badge.success {
  background: rgba(72, 187, 120, 0.2);
  border: 1px solid rgba(72, 187, 120, 0.4);
  color: #48bb78;
}

.qb-attachment-badge.failed {
  background: rgba(245, 101, 101, 0.2);
  border: 1px solid rgba(245, 101, 101, 0.4);
  color: #f56565;
}

.qb-attachment-badge i {
  margin: 0;
  font-size: 0.75rem;
}

.office-status-section {
  background: linear-gradient(135deg, rgba(43, 108, 176, 0.1) 0%, rgba(66, 153, 225, 0.05) 100%);
  border: 1px solid rgba(66, 153, 225, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.office-status-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(66, 153, 225, 0.2);
}

.office-logo-badge {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(66, 153, 225, 0.2);
  border: 2px solid rgba(66, 153, 225, 0.4);
  border-radius: 8px;
  color: #4299e1;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.office-status-content {
  flex: 1;
}

.office-status-content h5 {
  margin: 0 0 0.25rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.office-status-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.office-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
}

.office-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.office-info-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.office-info-value {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.attachments-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.attachments-section h5 {
  margin: 0 0 1.25rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header-with-action h5 {
  margin: 0;
  padding: 0;
  border: none;
}

.quickbooks-buttons-container {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
}

.already-sent-checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  cursor: pointer;
  margin: 0;
}

.quickbooks-button {
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  background: linear-gradient(135deg, #2ca01c 0%, #48bb78 100%);
  border: none;
  transition: all 0.2s ease;
}

.quickbooks-button.sandbox-button {
  background: linear-gradient(135deg, #4a5568 0%, #718096 100%);
}

.quickbooks-button.sandbox-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #718096 0%, #4a5568 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(113, 128, 150, 0.3);
}

.quickbooks-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #48bb78 0%, #2ca01c 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(44, 160, 28, 0.3);
}

.quickbooks-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.quickbooks-button.quickbooks-button-sent {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.quickbooks-button.quickbooks-button-sent:hover:not(:disabled) {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
}

.quickbooks-button.office-button {
  background: linear-gradient(135deg, #2b6cb0 0%, #4299e1 100%);
}

.quickbooks-button.office-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #4299e1 0%, #2b6cb0 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
}

.quickbooks-button.office-button-sent {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
}

.quickbooks-button.office-button-sent:hover:not(:disabled) {
  background: linear-gradient(135deg, #3182ce 0%, #2b6cb0 100%);
}

.quickbooks-button i {
  margin: 0;
}

.attachments-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  transition: background 0.2s ease;
}

.attachment-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.attachment-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(102, 204, 255, 0.1);
  border: 1px solid rgba(102, 204, 255, 0.3);
  border-radius: 6px;
  color: #66ccff;
  font-size: 1.25rem;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
}

.attachment-description {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
}

.attachment-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.attachment-action {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  white-space: nowrap;
}

.attachment-action i {
  margin-right: 0.5rem;
}

.attachment-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.attachment-unavailable {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  font-style: italic;
}

.attachment-not-needed {
  flex-shrink: 0;
  color: rgba(102, 204, 255, 0.8);
  font-size: 0.85rem;
  font-style: italic;
}

.attachment-item.upload-new {
  border: 1px dashed rgba(102, 204, 255, 0.3);
  background: rgba(102, 204, 255, 0.02);
}

.attachment-item.upload-new:hover {
  background: rgba(102, 204, 255, 0.05);
  border-color: rgba(102, 204, 255, 0.5);
}

.attachment-item.upload-new .attachment-icon {
  background: rgba(72, 187, 120, 0.1);
  border-color: rgba(72, 187, 120, 0.3);
  color: #48bb78;
}

.shifts-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
}

.shifts-section h5 {
  margin: 0 0 1.25rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.no-data {
  text-align: center;
  padding: 2rem 0;
  color: rgba(255, 255, 255, 0.5);
}

.no-data p {
  margin: 0;
  font-size: 1rem;
}

.shifts-table-container {
  overflow-x: auto;
}

.shifts-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.shifts-table thead {
  background: rgba(255, 255, 255, 0.05);
}

.shifts-table th {
  padding: 0.75rem;
  text-align: left;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.shifts-table td {
  padding: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.shifts-table tbody tr.clickable-row {
  cursor: pointer;
}

.shifts-table tbody tr.clickable-row:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.service-codes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.service-code-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: rgba(102, 204, 255, 0.2);
  border: 1px solid rgba(102, 204, 255, 0.3);
  border-radius: 4px;
  font-size: 0.75rem;
  color: #66ccff;
  font-weight: 600;
}

.hours-cell {
  font-weight: 600;
  color: #fff;
}

.notes-button {
  padding: 0.25rem 0.5rem;
  color: #66ccff;
  font-size: 0.9rem;
}

.notes-button:hover {
  color: #fff;
  background: rgba(102, 204, 255, 0.1);
}

.no-notes {
  color: rgba(255, 255, 255, 0.3);
}

.shifts-table tfoot {
  background: rgba(255, 255, 255, 0.05);
  border-top: 2px solid rgba(255, 255, 255, 0.2);
}

.total-row td {
  padding: 1rem 0.75rem;
  border-bottom: none;
}

.total-label {
  text-align: right;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
}

.total-value {
  font-weight: 700;
  color: #fff;
  font-size: 1.1rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: #1a1a2e;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header h5 {
  margin: 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  transition: color 0.2s ease;
}

.close-button:hover {
  color: #fff;
}

.modal-body {
  padding: 1.25rem;
}

.shift-info {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.shift-info p {
  margin: 0.5rem 0;
  color: rgba(255, 255, 255, 0.8);
}

.shift-info strong {
  color: #fff;
}

.notes-content {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* PDF Viewer Styles */
.pdf-viewer-container {
  height: 80vh;
  max-height: calc(85vh - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pdf-viewer-frame {
  width: 100%;
  height: 100%;
  border: none;
}

.no-pdf {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  padding: 2rem;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .section-header-with-action {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .quickbooks-button {
    width: 100%;
    justify-content: center;
  }

  .attachment-item {
    flex-wrap: wrap;
  }

  .attachment-info {
    flex-basis: 100%;
    order: 2;
  }

  .attachment-actions,
  .attachment-action,
  .attachment-unavailable {
    order: 3;
  }

  .attachment-actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .attachment-actions .attachment-action {
    flex: 1;
    min-width: 100px;
  }

  .shifts-table {
    font-size: 0.85rem;
  }

  .shifts-table th,
  .shifts-table td {
    padding: 0.5rem 0.25rem;
  }

  .modal-content {
    margin: 1rem;
  }

  .pdf-viewer-container {
    height: 60vh;
  }
}
</style>
